import { useState } from "react";
import PricingCard from "./PricingCard";
import api from '../Services/api';

interface PricingSectionProps {
  onSubscribe: (tier: string, interval: "month" | "year") => void;
}

const pricingTiers = [
  {
    name: "Basic",
    monthlyPrice: "$39/month",
    yearlyPrice: "$420/year",
    description:
      "Small or occasional importer/exporter.",
    features: [
      "Customs Invoice PDF Creation",
      "Basic Database (up to 50 SKUs, 10 of each trade partner)",
      "HS Code Lookup",
      "Email support",
    ],
    buttonText: "Get Started",
  },
  {
    name: "Professional",
    monthlyPrice: "$79/month",
    yearlyPrice: "$860/year",
    description:
      "Medium-sized businesses with regular cross-border transactions.",
    features: [
      "All Basic Tier features",
      "Manage USMCA status and 1-click USMCA creation",
      "Expanded SKU Database (up to 1000 SKUs, 100 of each trade partner)",
      "Save and storage of CCI's (up to 250 documents)",
    ],
    buttonText: "Get Started",
    highlighted: true,
  },
  {
    name: "Premium",
    monthlyPrice: "$199/month",
    yearlyPrice: "$2,160/year",
    description:
      "High-volume importers/exporters.",
    features: [
      "All Professional tier features",
      "Unlimited SKU & Trade Partner database",
      "Unlimited CCI Document Storage",
      "Branded document creation",
      "Priority Support"
    ],
    buttonText: "Get Started",
  },
];

export default function PricingSection({ onSubscribe }: PricingSectionProps) {
  const [selectedInterval, setSelectedInterval] = useState<"month" | "year">(
    "month"
  );
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const onPlanSelect = async (tier: string) => {
    setIsLoading(tier);
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/auth";
      return;
    }

    try {
      const response = await api.get(
        "/subscription/status",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { activeSubscription } = response.data;
      if (activeSubscription) {
        window.location.href = "/account";
        return;
      }
    } catch (error) {
      console.error("Error checking subscription status", error);
    }

    onSubscribe(tier.toLowerCase(), selectedInterval);
  };

  return (
    <div className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Simple, transparent pricing
          </h2>
          <div className="flex items-center justify-center mt-6">
            <button
              onClick={() => setSelectedInterval("month")}
              className={`px-6 py-2 border border-gray-300 text-sm font-medium rounded-l-full focus:outline-none transition-colors ${selectedInterval === "month"
                ? "bg-secondary text-white border-secondary"
                : "bg-gray-100 text-gray-800"
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedInterval("year")}
              className={`px-6 py-2 border border-gray-300 text-sm font-medium rounded-r-full focus:outline-none transition-colors ${selectedInterval === "year"
                ? "bg-secondary text-white border-secondary"
                : "bg-gray-100 text-gray-800"
                }`}
            >
              Yearly (Save 10%)
            </button>
          </div>
        </div>
        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-8 sm:mt-20 lg:grid-cols-3">
          {pricingTiers.map((tier, index) => (
            <PricingCard
              key={index}
              {...tier}
              price={
                selectedInterval === "month"
                  ? tier.monthlyPrice
                  : tier.yearlyPrice
              }
              onSelect={() => onPlanSelect(tier.name)}
              isLoading={isLoading === tier.name}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
